import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { useWindowVirtualizer } from '@tanstack/react-virtual';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useBreakpoint } from '~/techstyle-shared/react-components';

import {
  GAP_BETWEEN_GRID_COLUMNS_DESKTOP,
  GAP_BETWEEN_GRID_ROWS_DESKTOP,
  GAP_BETWEEN_GRID_ROWS_MOBILE,
  GRID_CARD_TYPES,
  VIRTUAL_SCROLL_OVERSCAN,
  VIRTUAL_SCROLL_GRID_ID,
  GAP_BETWEEN_GRID_COLUMNS_MOBILE,
} from '../constants';
import useDebouncedWindowEvent from '../hooks/useDebouncedWindowEvent';
import useNumGridCols from '../hooks/useNumGridCols';
import useShowIndexInfo from '../hooks/useShowIndexInfo';
import {
  getEstimatedCellHeight,
  getEstimatedCardWidth,
} from '../utils/measurements';

import GridCardRenderer from './GridCardRenderer';
import NoResultsFound from './NoResultsFound';
import ProductCardSkeleton from './ProductCard/ProductCardSkeleton';

const GridContainer = styled.div`
  width: 100%;
`;

const SkeletonGrid = styled.div<{
  $numGridCols: number;
  $isMobile: boolean;
}>`
  display: grid;
  grid-template-columns: repeat(${({ $numGridCols }) => $numGridCols}, 1fr);
  gap: ${({ $isMobile }) =>
    $isMobile
      ? `${GAP_BETWEEN_GRID_ROWS_MOBILE}px ${GAP_BETWEEN_GRID_COLUMNS_MOBILE}px`
      : `${GAP_BETWEEN_GRID_ROWS_DESKTOP}px ${GAP_BETWEEN_GRID_COLUMNS_DESKTOP}px`};
`;

const VirtualizerContainer = styled.div<{
  $totalSize: number;
}>`
  height: ${({ $totalSize }) => $totalSize}px;
  position: relative;
`;

const VirtualRow = styled.div<{
  $rowStart: number;
}>`
  position: absolute;
  top: 0;
  transform: translateY(${({ $rowStart }) => $rowStart}px);
  width: 100%;
`;

const RowGrid = styled.div<{
  $numGridCols: number;
  $isMobile: boolean;
}>`
  display: grid;
  grid-template-columns: repeat(${({ $numGridCols }) => $numGridCols}, 1fr);
  gap: ${({ $isMobile }) =>
    $isMobile
      ? `${GAP_BETWEEN_GRID_ROWS_MOBILE}px ${GAP_BETWEEN_GRID_COLUMNS_MOBILE}px`
      : `${GAP_BETWEEN_GRID_ROWS_DESKTOP}px ${GAP_BETWEEN_GRID_COLUMNS_DESKTOP}px`};
`;

// POC Reference: https://github.com/Fabletics/web-spa/pull/3883/files#diff-dfe3140fb2e0ace5e8430d5ba8ce5c99925d14e33102bc49717d1373b7d340f3

// IMPORTANT: make sure to not use expensive operations in this component. This component re-renders
// on scroll and resize per the virtualizer. There are optimizations with the virtualizer that does
// not re-paint the entire grid but it will re-render the items in view so avoid expensive operations
// in this component. This is especially true for any hooks, even if the hook has caching mechanisms.
// The hook will still be called on every render, so avoid hooks in this component
// If you need to use hooks, use them in the parent component and pass down the data.
const VirtualScrollGrid = ({
  fetchNextPage = () => {},
  hasNextPage,
  isPrimaryProductsFetching,
  isPrimaryProductsLoading,
  isRelatedProductsFetching,
  isProductPricingFetching,
  virtualList = [],
  setIsFiltersPaneOpen,
}) => {
  const numGridCols = useNumGridCols();
  const { isMobile } = useBreakpoint();
  const showIndexInfo = useShowIndexInfo();
  const firstProductCardIdRef = useRef(null);

  const rowGap = isMobile
    ? GAP_BETWEEN_GRID_ROWS_MOBILE
    : GAP_BETWEEN_GRID_ROWS_DESKTOP;

  // Estimate card width from the current viewport to size skeletons and virtualizer estimates.
  const estimatedCardWidth = getEstimatedCardWidth({
    isMobile,
    numGridCols,
    viewportWidth: document?.documentElement?.clientWidth,
  });

  // Used for initial virtualizer estimates and skeleton sizing before real measurements.
  const estimatedCardHeight = getEstimatedCellHeight({
    isMobile,
    dynamicCardWidth: estimatedCardWidth,
  });

  const getEstimatedRowHeight = (index) => {
    const virtualRowItems = virtualList[index]; // each virtualRow will actually be rendered as a grid of items
    const assets = virtualRowItems.filter(
      (item) => item.type === GRID_CARD_TYPES.ASSET
    );

    if (!assets?.length) {
      return estimatedCardHeight; // rowSpan = 1 for non-asset items (product cards)
    }
    const minAssetRowIndex = Math.min(
      ...assets.map((item) => item.effectiveCustomVars?.row)
    );
    const maxAssetRowIndex = Math.max(
      ...assets.map((item) => item.effectiveCustomVars?.row + item.span.rows)
    );
    const rowSpan = maxAssetRowIndex - minAssetRowIndex;

    const rowHeight = estimatedCardHeight * rowSpan + rowGap * (rowSpan - 1);

    return rowHeight;
  };

  // Cache measured row heights by row index. We only read the DOM the first
  // time a row appears — subsequent remounts (during scroll) return the cached
  // value. This avoids per-scroll layout reads that churn `getTotalSize()` and
  // interrupt smooth scroll / cause resize lag.
  const measuredRowHeights = useRef<Map<number, number>>(new Map());

  const virtualizer = useWindowVirtualizer({
    count: virtualList?.length || 0,
    // Cache-first size lookup — measured rows return their real height,
    // unmeasured rows fall back to the estimate derived from card width +
    // aspect ratio. No `measureElement` is wired up because the ResizeObserver
    // it attaches fires on every scroll and interrupts smooth scroll-to-top
    // (measurements mid-animation shift getTotalSize). Instead, each row is
    // measured exactly once via a plain callback ref — see `captureRowRef`
    // below — and the batch is flushed into the virtualizer only when the
    // data settles.
    estimateSize: (index) => {
      const cached = measuredRowHeights.current.get(index);
      if (cached !== undefined) {
        return cached;
      }
      return getEstimatedRowHeight(index);
    },
    gap: rowGap,
    overscan: VIRTUAL_SCROLL_OVERSCAN,
  });

  // Measure every currently-mounted row, write its real height into the
  // cache, and tell the virtualizer to recompute positions. Uses a DOM
  // query (no ResizeObserver / no per-mount ref) so nothing fires during
  // scroll. Runs on a frame so layout has committed before we read.
  const recalcRows = useCallback(
    ({ clearCache = false }: { clearCache?: boolean } = {}) => {
      const frameId = requestAnimationFrame(() => {
        if (clearCache) {
          measuredRowHeights.current.clear();
        }
        const container = document.getElementById(VIRTUAL_SCROLL_GRID_ID);
        const rows =
          container?.querySelectorAll<HTMLElement>('[data-index]') ?? [];
        rows.forEach((row) => {
          const index = Number(row.dataset.index);
          const height = row.getBoundingClientRect().height;
          if (Number.isFinite(index) && height > 0) {
            measuredRowHeights.current.set(index, height);
          }
        });
        virtualizer.measure();
      });
      return () => cancelAnimationFrame(frameId);
    },
    [virtualizer]
  );

  const virtualItems = virtualizer.getVirtualItems();

  // IMPORTANT: `virtualList` MUST have a stable reference across parent
  // re-renders when its contents are unchanged. A new-array-per-render
  // upstream will fire this effect on every scroll-driven parent render,
  // wipe the row-height cache, and cause scroll-to-top to stall row-by-row
  // as each row re-measures mid-animation. `useInGridAssets` memoizes
  // `mergedAssetsAndProducts` for this reason — any future upstream hook
  // feeding `virtualList` must preserve reference equality when content is
  // unchanged (use `useMemo` on the returned array).
  //
  // Recalc triggers (all safe outside of scroll):
  //   - list identity change (filter/sort)
  //   - any fetch flag flip (new content enriches cells)
  //   - viewport width change
  //   - scroll-end (rows mounted during scroll get real measurements)
  useLayoutEffect(() => {
    return recalcRows({ clearCache: true });
  }, [virtualList, recalcRows]);

  // Skip recalc while a scroll (e.g. smooth scroll-to-top) is in flight — row
  // re-measurement mid-animation shifts positions and yanks the scroll
  // container, causing jitter/stall. The scroll-end debounced handler below
  // will pick up any deferred work when the scroll settles.
  useEffect(() => {
    if (virtualizer.isScrolling) {
      return undefined;
    }
    return recalcRows();
  }, [
    isPrimaryProductsFetching,
    isRelatedProductsFetching,
    isProductPricingFetching,
    recalcRows,
    virtualizer.isScrolling,
  ]);

  // Cards call this when their active product changes — height can shift
  // (e.g. swatch select adds promo labels) and `virtualList` identity / fetch
  // flags don't flip, so without this trigger the row keeps its stale cached
  // height until the user scrolls.
  //
  // Skip while a scroll is in flight (smooth scroll-to-top, mobile momentum)
  // for the same reason the fetch-flag effect above bails: re-measurement
  // mid-scroll shifts `getTotalSize()` and jitters the container. The
  // scroll-end debounced handler below will pick up the stale row 150ms after
  // scrolling settles.
  const onActiveProductChange = useCallback(() => {
    if (virtualizer.isScrolling) {
      return;
    }
    recalcRows();
  }, [virtualizer, recalcRows]);

  // Width-change recalc (not height — mobile URL-bar collapse fires `resize`
  // with the same clientWidth during scroll and would otherwise wipe the
  // cache mid-animation). Scroll-end recalc — after smooth scroll-to-top or
  // manual scrolling stops, measure whatever is on screen now. Timer keeps
  // resetting while the event stream is active, so neither fires mid-scroll.
  useDebouncedWindowEvent({
    event: 'resize',
    delayMs: 150,
    widthChangeOnly: true,
    handler: useCallback(() => recalcRows({ clearCache: true }), [recalcRows]),
  });
  useDebouncedWindowEvent({
    event: 'scroll',
    delayMs: 150,
    passive: true,
    handler: useCallback(() => recalcRows(), [recalcRows]),
  });

  // Store fetchNextPage in a ref so its reference changes (which happen on
  // every React Query data update) don't re-trigger the pagination effect.
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;

  // Stable callback that delegates to the latest fetchNextPage without
  // changing identity — prevents the effect from firing on every data update.
  const stableFetchNextPage = useCallback(() => {
    fetchNextPageRef.current();
  }, []);

  // Guard against duplicate fetches. Stores both the list length AND a snapshot
  // of all item IDs so it can distinguish between two cases that produce the
  // same length:
  //   1. The gap between primary resolving and GQL supplements merging
  //      (same length, same IDs) → should block
  //   2. A filter change that returns the same page size as the previous query
  //      (same length, different IDs) → should NOT block
  //
  // Note: keepPreviousData: true on the primary queries means isPrimaryProductsLoading
  // stays false on filter change — a length-only guard can't distinguish these cases.
  // Cost is trivial: typical grids have 24–96 product IDs to compare.
  const fetchedAtRef = useRef<{
    length: number;
    ids: (string | number)[];
  } | null>(null);

  useEffect(() => {
    if (virtualItems.length === 0) {
      return;
    }

    const lastItem = virtualItems.at(-1);
    if (!lastItem) {
      return;
    }

    // Short-circuit: if length differs, it's a different list. Skip the
    // per-id comparison entirely.
    const previousFetchState = fetchedAtRef.current;
    const lengthMatches =
      previousFetchState !== null &&
      previousFetchState.length === virtualList.length;

    let alreadyFetchedForThisState = false;
    if (lengthMatches) {
      const currentIds = virtualList.flatMap((row) =>
        row.map((item) => item.id)
      );
      alreadyFetchedForThisState =
        previousFetchState!.ids.length === currentIds.length &&
        previousFetchState!.ids.every((id, i) => id === currentIds[i]);
    }

    const shouldFetchNextPage =
      lastItem.index >= virtualList?.length - 1 &&
      hasNextPage &&
      !isPrimaryProductsFetching &&
      !alreadyFetchedForThisState;

    if (shouldFetchNextPage) {
      fetchedAtRef.current = {
        length: virtualList.length,
        ids: virtualList.flatMap((row) => row.map((item) => item.id)),
      };
      stableFetchNextPage();
    }
  }, [
    hasNextPage,
    isPrimaryProductsFetching,
    stableFetchNextPage,
    virtualItems,
    virtualList,
  ]);

  if (isPrimaryProductsLoading) {
    return (
      <GridContainer>
        <SkeletonGrid $numGridCols={numGridCols} $isMobile={isMobile}>
          {Array.from({ length: numGridCols * 3 }).map((_, index) => {
            return (
              <ProductCardSkeleton key={index} height={estimatedCardHeight} />
            );
          })}
        </SkeletonGrid>
      </GridContainer>
    );
  }

  if (!virtualList?.length) {
    return (
      <GridContainer>
        <NoResultsFound setIsFiltersPaneOpen={setIsFiltersPaneOpen} />
      </GridContainer>
    );
  }

  return (
    <GridContainer>
      <VirtualizerContainer
        $totalSize={virtualizer.getTotalSize()}
        id={VIRTUAL_SCROLL_GRID_ID}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const rowIndex = virtualRow.index;
          const rowItems = virtualList[rowIndex];

          return (
            <VirtualRow
              // Keep a stable React key to avoid unmounting row contents on resize.
              key={rowIndex}
              data-index={rowIndex}
              $rowStart={virtualRow.start}
            >
              <RowGrid $numGridCols={numGridCols} $isMobile={isMobile}>
                {rowItems?.map((item, column) => {
                  const isAsset = item?.type === GRID_CARD_TYPES.ASSET;
                  if (firstProductCardIdRef.current === null && !isAsset) {
                    firstProductCardIdRef.current = item.id;
                  }
                  const isFirstProductCard =
                    item.id === firstProductCardIdRef.current;
                  return (
                    <GridCardRenderer
                      key={item.id}
                      item={item}
                      row={rowIndex}
                      column={column}
                      isFirstProductCard={isFirstProductCard}
                      showIndexInfo={showIndexInfo}
                      onActiveProductChange={onActiveProductChange}
                    />
                  );
                })}
              </RowGrid>
            </VirtualRow>
          );
        })}
      </VirtualizerContainer>
    </GridContainer>
  );
};

VirtualScrollGrid.propTypes = {
  fetchNextPage: PropTypes.func,
  hasNextPage: PropTypes.bool,
  isPrimaryProductsFetching: PropTypes.bool,
  isPrimaryProductsLoading: PropTypes.bool,
  isProductPricingFetching: PropTypes.bool,
  isRelatedProductsFetching: PropTypes.bool,
  setIsFiltersPaneOpen: PropTypes.func.isRequired,
  virtualList: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        type: PropTypes.string,
        span: PropTypes.shape({
          rows: PropTypes.number,
          columns: PropTypes.number,
        }),
      })
    )
  ),
};

export default VirtualScrollGrid;
